<template>
  <Table>
    <TableHeader>
      <TableRow>
        <TableHead
          v-for="col in columns"
          :key="col.key"
          :class="[col.hidden ? 'relative' : undefined, col.headClass]"
          :aria-sort="sortedRows?.length ? ariaSort(col) : undefined"
        >
          <slot :name="`head-${col.key}`" :col="col" :sort="doSort">
            <template v-if="col.hidden">
              <span class="sr-only">{{ col.label }}</span>
            </template>
            <template v-else-if="col.sortable">
              <SortButton :aria-disabled="!sortedRows?.length" @click="doSort(col)">
                {{ col.label }}
              </SortButton>
            </template>
            <template v-else>
              {{ col.label }}
            </template>
          </slot>
        </TableHead>
      </TableRow>
    </TableHeader>
    <TableBody>
      <template v-if="sortedRows?.length > 0">
        <TableRow v-for="row in sortedRows" :key="row.id" @click="row.onClick?.()" :class="row.rowClass ?? ''">
          <TableCell v-for="col in columns" :key="col.key" :class="col.cellClass ?? ''">
            <slot :name="`col-${col.key}`" :row="row" :data="row[col.key]">
              {{ row[col.key] }}
            </slot>
          </TableCell>
        </TableRow>
      </template>
      <template v-else>
        <slot name="default" />
      </template>
    </TableBody>
  </Table>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { SortButton } from ".";
import Table from "./Table.vue";
import TableBody from "./TableBody.vue";
import TableCell from "./TableCell.vue";
import TableHead from "./TableHead.vue";
import TableHeader from "./TableHeader.vue";
import TableRow from "./TableRow.vue";
import globals from "../../../globals";

const props = defineProps({
  columns: {
    type: Array,
    required: true,
    // Example: [
    // {
    //    key: 'name',
    //    label: 'Name',
    //    sortable: true,
    //    hidden: false,
    //    cellClass: 'relative py-4 pl-3 pr-4 text-right font-medium',
    //    headClass: 'text-right',
    // }]
  },
  rows: {
    type: Array,
    required: true,
  },
  sort: {
    type: Function,
    default: null,
    // Should accept (key: string) and modify the bound array externally
  },
  sortedColKey: {
    type: String,
    default: null,
  },
  sortedColDescending: {
    type: Boolean,
    default: null,
  },
});

const sortBy = ref(null);
const sortDesc = ref(false);

function ariaSort(col) {
  if (props.sort !== null) {
    return props.sortedColKey === col.key ? (props.sortedColDescending ? "descending" : "ascending") : undefined;
  }
  return sortBy.value === col.key ? (sortDesc.value ? "descending" : "ascending") : undefined;
}

const sortedRows = computed(() => {
  if (props.sort !== null) {
    return props.rows;
  }
  if (!sortBy.value) {
    return props.rows;
  }

  const sorted = props.rows.toSorted(globals.smartSortFunc(globals.sortObjBy(sortBy.value)));
  return sortDesc.value ? sorted.reverse() : sorted;
});

function doSort(col) {
  if (props.sort !== null) {
    return props.sort(col);
  }
  return handleSortChange(col);
}

function handleSortChange(col) {
  if (sortBy.value === col.key) {
    sortDesc.value = !sortDesc.value;
    return sortDesc.value;
  }

  sortBy.value = col.key;
  sortDesc.value = false;

  return sortDesc.value;
}

onMounted(() => {
  if (
    !(Boolean(props.sort) && Boolean(props.sortedColKey) && Boolean(props.sortedColDescending)) &&
    (Boolean(props.sort) || Boolean(props.sortedColKey) || Boolean(props.sortedColDescending))
  ) {
    throw Error("AutoTable should have sort, sortedColKey, and sortedColDescending provided together");
  }
});
</script>
